import React, { useState, useEffect, useMemo } from 'react';
import { adminApi } from '../services/api';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import {
  Cpu, HardDrive, Clock, ArrowUpDown, RefreshCw, Download,
  Calendar, ChevronDown, Database
} from 'lucide-react';
import toast from 'react-hot-toast';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];

const PRESET_RANGES = [
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'This month', value: 'month' },
  { label: 'Last 3 months', value: '90d' },
  { label: 'Custom', value: 'custom' },
];

function getDateRange(preset) {
  const now = new Date();
  const to = now.toISOString().split('T')[0];
  let from;

  switch (preset) {
    case 'today':
      from = to;
      break;
    case '7d':
      from = new Date(now.setDate(now.getDate() - 7)).toISOString().split('T')[0];
      break;
    case 'month': {
      const d = new Date();
      from = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
      break;
    }
    case '90d':
      from = new Date(now.setDate(now.getDate() - 90)).toISOString().split('T')[0];
      break;
    case '30d':
    default:
      from = new Date(now.setDate(now.getDate() - 30)).toISOString().split('T')[0];
      break;
  }

  return { from, to: new Date().toISOString().split('T')[0] };
}

function formatCost(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function formatHours(value) {
  return `${Number(value || 0).toFixed(2)} hrs`;
}

function formatGB(value) {
  const gb = Number(value || 0);
  if (gb < 0.01) return `${(gb * 1024).toFixed(2)} MB`;
  return `${gb.toFixed(2)} GB`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  // Handle both "2026-03-01" and "2026-03-01T00:00:00Z" formats
  const datePart = String(dateStr).split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

/**
 * NeonUsagePanel - Reusable component for displaying Neon database usage metrics.
 *
 * @param {string} tenantId - If provided, shows usage for a single tenant
 * @param {boolean} showTenantBreakdown - If true, shows per-tenant table (for analytics page)
 * @param {string} className - Additional CSS classes
 */
const NeonUsagePanel = ({ tenantId = null, showTenantBreakdown = false, className = '' }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [rangePreset, setRangePreset] = useState('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [granularity, setGranularity] = useState('daily');
  const [sortField, setSortField] = useState('total_cost');
  const [sortDir, setSortDir] = useState('desc');
  const [refreshing, setRefreshing] = useState(false);
  const [collecting, setCollecting] = useState(false);

  const dateRange = useMemo(() => {
    if (rangePreset === 'custom') {
      return { from: customFrom, to: customTo };
    }
    return getDateRange(rangePreset);
  }, [rangePreset, customFrom, customTo]);

  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      fetchData();
    }
  }, [dateRange, granularity, tenantId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        from: dateRange.from,
        to: dateRange.to,
        granularity,
      };

      let response;
      if (tenantId) {
        // For tenant pages, try stored first, then fall back to live
        try {
          params.source = 'stored';
          response = await adminApi.getTenantNeonUsage(tenantId, params);
          // If stored has no data, try live
          const d = response.data.data;
          if (!d || (!d.logs?.length && (!d.totals || Number(d.totals.grand_total) === 0))) {
            params.source = 'live';
            response = await adminApi.getTenantNeonUsage(tenantId, params);
          }
        } catch {
          // If stored fails (table doesn't exist), try live
          params.source = 'live';
          response = await adminApi.getTenantNeonUsage(tenantId, params);
        }
      } else {
        // For analytics page, try stored first, then fall back to live
        try {
          params.source = 'stored';
          response = await adminApi.getNeonUsage(params);
          const d = response.data.data;
          if (!d || (!d.tenants?.length && (!d.totals || Number(d.totals.grand_total) === 0))) {
            params.source = 'live';
            response = await adminApi.getNeonUsage(params);
          }
        } catch {
          params.source = 'live';
          response = await adminApi.getNeonUsage(params);
        }
      }

      setData(response.data.data);
    } catch (err) {
      console.error('Failed to fetch Neon usage:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleCollect = async () => {
    setCollecting(true);
    try {
      if (tenantId) {
        // For a single tenant, just fetch live data from Neon API
        const params = {
          from: dateRange.from,
          to: dateRange.to,
          granularity,
          source: 'live',
        };
        const response = await adminApi.getTenantNeonUsage(tenantId, params);
        setData(response.data.data);
        toast.success('Live data fetched from Neon');
      } else {
        // For all tenants, fetch live data from Neon API
        const params = {
          from: dateRange.from,
          to: dateRange.to,
          granularity,
          source: 'live',
        };
        const response = await adminApi.getNeonUsage(params);
        setData(response.data.data);
        toast.success('Live data fetched from Neon');
      }
    } catch (err) {
      console.error('Collect error:', err);
      toast.error(err.response?.data?.error || 'Failed to collect usage data');
    } finally {
      setCollecting(false);
    }
  };

  const handleExportCSV = () => {
    if (!data) return;

    let csvRows = [];

    if (tenantId && data.logs) {
      csvRows.push(['Date', 'Compute Hours', 'Compute Cost', 'Storage GB', 'Storage Cost', 'History GB', 'History Cost', 'Transfer GB', 'Transfer Cost', 'Total Cost']);
      for (const row of data.logs) {
        csvRows.push([
          row.period_date, row.compute_hours, row.compute_cost,
          row.storage_gb, row.storage_cost, row.history_gb, row.history_cost,
          row.data_transfer_gb, row.transfer_cost, row.total_cost,
        ]);
      }
    } else if (data.tenants) {
      csvRows.push(['Tenant', 'Compute Hours', 'Compute Cost', 'Storage GB', 'Storage Cost', 'History GB', 'History Cost', 'Transfer GB', 'Transfer Cost', 'Total Cost']);
      for (const t of data.tenants) {
        csvRows.push([
          t.firm_name || t.name || t.slug,
          t.compute_hours, t.compute_cost,
          t.storage_gb, t.storage_cost, t.history_gb, t.history_cost,
          t.transfer_gb, t.transfer_cost, t.total_cost,
        ]);
      }
    }

    const csv = csvRows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `neon-usage-${dateRange.from}-to-${dateRange.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sortedTenants = useMemo(() => {
    if (!data?.tenants) return [];
    return [...data.tenants].sort((a, b) => {
      const aVal = Number(a[sortField] || 0);
      const bVal = Number(b[sortField] || 0);
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [data?.tenants, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortHeader = ({ field, children }) => (
    <th
      className="table-header cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={`w-3 h-3 ${sortField === field ? 'text-primary-600' : 'text-gray-400'}`} />
      </div>
    </th>
  );

  const totals = data?.totals;
  const chartData = tenantId ? data?.logs : data?.trend;

  // Pie chart data for cost breakdown
  const pieData = totals ? [
    { name: 'Compute', value: Number(totals.total_compute_cost || 0) },
    { name: 'Storage', value: Number(totals.total_storage_cost || 0) },
    { name: 'History', value: Number(totals.total_history_cost || 0) },
    { name: 'Transfer', value: Number(totals.total_transfer_cost || 0) },
  ].filter(d => d.value > 0) : [];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">Database Usage</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Date range selector */}
          <select
            value={rangePreset}
            onChange={(e) => setRangePreset(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {PRESET_RANGES.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>

          {rangePreset === 'custom' && (
            <>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
              />
            </>
          )}

          {/* Granularity */}
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
          </select>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={handleExportCSV}
            disabled={!data}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Export CSV"
          >
            <Download className="w-4 h-4" />
          </button>

          {showTenantBreakdown && (
            <button
              onClick={handleCollect}
              disabled={collecting}
              className="text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {collecting ? 'Collecting...' : 'Sync from Neon'}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : !data || (!data.totals && !data.logs?.length) ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No usage data available for this period</p>
          <p className="text-sm text-gray-400 mt-1">Click "Sync from Neon" to collect data from the Neon API</p>
          {!showTenantBreakdown && (
            <button
              onClick={handleCollect}
              disabled={collecting}
              className="mt-3 text-sm px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {collecting ? 'Collecting...' : 'Sync from Neon'}
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          {totals && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-blue-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Cpu className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-medium text-blue-700 uppercase">Compute</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{formatHours(totals.total_compute_hours)}</p>
                <p className="text-sm font-semibold text-blue-600">{formatCost(totals.total_compute_cost)}</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <HardDrive className="w-4 h-4 text-green-600" />
                  <span className="text-xs font-medium text-green-700 uppercase">Storage</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{formatGB(totals.total_storage_gb)}</p>
                <p className="text-sm font-semibold text-green-600">{formatCost(totals.total_storage_cost)}</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-medium text-amber-700 uppercase">History</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{formatGB(totals.total_history_gb)}</p>
                <p className="text-sm font-semibold text-amber-600">{formatCost(totals.total_history_cost)}</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpDown className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-medium text-purple-700 uppercase">Transfer</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{formatGB(totals.total_transfer_gb)}</p>
                <p className="text-sm font-semibold text-purple-600">{formatCost(totals.total_transfer_cost)}</p>
              </div>
              <div className="bg-gray-100 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-700 uppercase">Total</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{formatCost(totals.grand_total)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {dateRange.from} to {dateRange.to}
                </p>
              </div>
            </div>
          )}

          {/* Charts Row */}
          {chartData && chartData.length > 0 && (() => {
            const mappedData = chartData.map(d => ({
              ...d,
              date: formatDate(d.period_date),
              computeCost: Number(d.compute_cost),
              storageCost: Number(d.storage_cost),
              historyCost: Number(d.history_cost),
              transferCost: Number(d.transfer_cost),
              computeHrs: Number(d.compute_hours),
              storageGb: Number(d.storage_gb || d.storage_cost),
              historyGb: Number(d.history_gb || d.history_cost),
              transferGb: Number(d.data_transfer_gb || d.transfer_gb || 0),
            }));
            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Cost Trend Chart */}
                <div className="card p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Cost Trend</h4>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={mappedData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        formatter={(value, name) => [formatCost(value), name]}
                        labelFormatter={(label) => label}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="computeCost" name="Compute" fill="#3b82f6" stackId="cost" />
                      <Bar dataKey="storageCost" name="Storage" fill="#10b981" stackId="cost" />
                      <Bar dataKey="historyCost" name="History" fill="#f59e0b" stackId="cost" />
                      <Bar dataKey="transferCost" name="Transfer" fill="#8b5cf6" stackId="cost" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Compute Hours Chart */}
                <div className="card p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Compute Hours</h4>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={mappedData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} hrs`} />
                      <Tooltip
                        formatter={(value) => [formatHours(value), 'Compute']}
                        labelFormatter={(label) => label}
                      />
                      <Bar dataKey="computeHrs" name="Compute Hours" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Storage & Transfer Chart */}
                <div className="card p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Storage, History & Transfer</h4>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={mappedData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} GB`} />
                      <Tooltip
                        formatter={(value, name) => [formatGB(value), name]}
                        labelFormatter={(label) => label}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="storageGb" name="Storage" stroke="#10b981" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="historyGb" name="History" stroke="#f59e0b" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="transferGb" name="Transfer" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Cost Breakdown Pie */}
                {pieData.length > 0 && (
                  <div className="card p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Cost Breakdown</h4>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={95}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCost(value)} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Daily/Monthly detail table */}
          {tenantId && data.logs && data.logs.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h4 className="text-sm font-semibold text-gray-700">
                  {granularity === 'daily' ? 'Daily' : 'Monthly'} Breakdown
                </h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="table-header">Date</th>
                      <th className="table-header text-right">Compute</th>
                      <th className="table-header text-right">Storage</th>
                      <th className="table-header text-right">History</th>
                      <th className="table-header text-right">Transfer</th>
                      <th className="table-header text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.logs.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="table-cell font-medium">{formatDate(row.period_date)}</td>
                        <td className="table-cell text-right">
                          <div>{formatHours(row.compute_hours)}</div>
                          <div className="text-xs text-gray-400">{formatCost(row.compute_cost)}</div>
                        </td>
                        <td className="table-cell text-right">
                          <div>{formatGB(row.storage_gb)}</div>
                          <div className="text-xs text-gray-400">{formatCost(row.storage_cost)}</div>
                        </td>
                        <td className="table-cell text-right">
                          <div>{formatGB(row.history_gb)}</div>
                          <div className="text-xs text-gray-400">{formatCost(row.history_cost)}</div>
                        </td>
                        <td className="table-cell text-right">
                          <div>{formatGB(row.data_transfer_gb)}</div>
                          <div className="text-xs text-gray-400">{formatCost(row.transfer_cost)}</div>
                        </td>
                        <td className="table-cell text-right font-semibold">{formatCost(row.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Per-tenant breakdown table (for analytics) */}
          {showTenantBreakdown && sortedTenants.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h4 className="text-sm font-semibold text-gray-700">Cost by Tenant</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="table-header">Tenant</th>
                      <SortHeader field="compute_hours">Compute Hrs</SortHeader>
                      <SortHeader field="compute_cost">Compute $</SortHeader>
                      <SortHeader field="storage_gb">Storage</SortHeader>
                      <SortHeader field="storage_cost">Storage $</SortHeader>
                      <SortHeader field="history_gb">History</SortHeader>
                      <SortHeader field="transfer_gb">Transfer</SortHeader>
                      <SortHeader field="total_cost">Total Cost</SortHeader>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedTenants.map((t, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="table-cell">
                          <div className="font-medium text-gray-900">{t.firm_name || t.name || t.slug}</div>
                          <div className="text-xs text-gray-400">{t.slug}</div>
                        </td>
                        <td className="table-cell text-right">{formatHours(t.compute_hours)}</td>
                        <td className="table-cell text-right text-blue-600 font-medium">{formatCost(t.compute_cost)}</td>
                        <td className="table-cell text-right">{formatGB(t.storage_gb)}</td>
                        <td className="table-cell text-right text-green-600 font-medium">{formatCost(t.storage_cost)}</td>
                        <td className="table-cell text-right">{formatGB(t.history_gb)}</td>
                        <td className="table-cell text-right">{formatGB(t.transfer_gb)}</td>
                        <td className="table-cell text-right font-bold">{formatCost(t.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 font-semibold">
                      <td className="table-cell">Total ({sortedTenants.length} tenants)</td>
                      <td className="table-cell text-right">{formatHours(totals?.total_compute_hours)}</td>
                      <td className="table-cell text-right text-blue-600">{formatCost(totals?.total_compute_cost)}</td>
                      <td className="table-cell text-right">{formatGB(totals?.total_storage_gb)}</td>
                      <td className="table-cell text-right text-green-600">{formatCost(totals?.total_storage_cost)}</td>
                      <td className="table-cell text-right">{formatGB(totals?.total_history_gb)}</td>
                      <td className="table-cell text-right">{formatGB(totals?.total_transfer_gb)}</td>
                      <td className="table-cell text-right text-lg">{formatCost(totals?.grand_total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NeonUsagePanel;
